import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Award, Users, ArrowRight } from "lucide-react";
import Layout from "../components/Layout";
import Breadcrumbs from "../components/Breadcrumbs";
import PreFooterCTA from "../components/PreFooterCTA";
import ServiceAreaMap from "../components/ServiceAreaMap";
import { BUSINESS, WHY_CHOOSE } from "../data/business";

const BREADCRUMBS = [{ label: "About" }];

const TEAM = [
  { name: "Mike Ragland", role: "Drain · Plumbing · HVAC Technician · 30+ years" },
  { name: "Leroy Maxwell", role: "Drain · Plumbing · HVAC Technician · 30+ years" },
];

export default function About() {
  return (
    <Layout>
      <section className="bg-[#1A1D21] text-white">
        <div className="abc-container py-12 md:py-16">
          <Breadcrumbs items={BREADCRUMBS} />
          <h1 className="font-display mt-4 text-4xl sm:text-5xl font-extrabold">Local team. Honest work. <span className="text-[#E31D2B]">No drama.</span></h1>
          <p className="mt-4 text-white/80 max-w-2xl text-lg">With <span className="text-white font-semibold">30+ years of experience</span>, Drain Sights is a drain &amp; sewer specialist serving two Maryland metros — Baltimore and Temple Hills / DC. Camera inspection, hydro-jetting, descaling, sewer &amp; water line work, and basement-water solutions for homeowners across both areas.</p>
        </div>
      </section>

      <section className="py-12 md:py-16 abc-bg-pale">
        <div className="abc-container grid lg:grid-cols-2 gap-12 items-center">
          <img src="/drain-sights-truck.png"
            alt="Drain Sights branded service truck" className="rounded-3xl shadow-xl w-full" loading="lazy" />
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#54616E] mb-3">Our story</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#1A1D21]">Built by neighbors, for neighbors.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">We're a small, focused drain &amp; sewer team with crews in the Baltimore and Temple Hills areas. We keep the crews small on purpose — every job is handled by a technician who actually cares whether it's done right.</p>
            <p className="mt-3 text-slate-600 leading-relaxed">We're licensed, insured, and proud to be locally run. We answer the phone 24/7 — call any time, day or night, and you'll talk to a real person. When we arrive, you'll get a clear price before any work begins.</p>
            <div className="mt-6 grid grid-cols-3 gap-3 text-center">
              <div className="abc-card p-4"><div className="font-display font-extrabold text-2xl text-[#1A1D21]">2 Hubs</div><div className="text-xs text-slate-500 uppercase tracking-wider mt-1">MD &amp; DC metros</div></div>
              <div className="abc-card p-4"><div className="font-display font-extrabold text-2xl text-[#1A1D21]">Upfront</div><div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Written quotes</div></div>
              <div className="abc-card p-4"><div className="font-display font-extrabold text-2xl text-[#1A1D21]">Clean</div><div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Workmanship</div></div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="abc-container">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#1A1D21] max-w-xl">What we stand for</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CHOOSE.map((w) => (
              <div key={w.title} className="abc-card p-6">
                <ShieldCheck className="w-5 h-5 text-[#54616E]" />
                <h3 className="font-display font-bold text-lg text-[#1A1D21] mt-3">{w.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 abc-bg-pale">
        <div className="abc-container text-center max-w-2xl mx-auto">
          <Users className="w-8 h-8 text-[#54616E] mx-auto" />
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#1A1D21] mt-4">Meet the team</h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Mike Ragland and Leroy Maxwell have been working in the drain cleaning, plumbing, and HVAC field for more than 30 years. As two knowledgeable technicians who are proficient at all drain cleaning systems, they are able to repair or overhaul any existing problems in their field. Not only are they willing to solve your issues, they teach you how to troubleshoot your draining issues. As natives of the Maryland area, we are willing to come to you and help wherever you need us.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-5 text-left max-w-xl mx-auto">
            {TEAM.map((t) => (
              <div key={t.name} className="abc-card p-6">
                <div className="w-14 h-14 rounded-2xl bg-[#EDEFF2] inline-flex items-center justify-center mb-3"><Award className="w-6 h-6 text-[#54616E]" /></div>
                <div className="font-display font-bold text-[#1A1D21]">{t.name}</div>
                <div className="text-sm text-slate-500">{t.role}</div>
              </div>
            ))}
          </div>
          <Link to="/services" className="abc-btn abc-btn-outline mt-8" data-testid="about-services-link">Explore our services <ArrowRight className="w-4 h-4" /></Link>
          <p className="text-xs text-slate-400 mt-4">Proudly serving the Baltimore and Temple Hills / DC metros.</p>
        </div>
      </section>

      <section className="py-12 md:py-16" data-testid="about-service-map">
        <div className="abc-container">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-widest text-[#54616E] mb-3">Where we work</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#1A1D21]">Two metros, one team you can trust</h2>
            <p className="mt-3 text-slate-600">We run two crews — one in the Baltimore area, one in the Temple Hills / DC area. Red pins are the cities we serve; the shaded band is our combined coverage.</p>
          </div>
          <div className="mt-8">
            <ServiceAreaMap />
          </div>
        </div>
      </section>

      <PreFooterCTA />
    </Layout>
  );
}
